import { HttpParams } from '@angular/common/http'
import type { AvailableInclude } from '../types/includes'
import type { Query } from '../types/query'
import { RequestParams } from '../types/request-params'
import { buildRequestParams } from './params'

interface Paging {
  length: number
  page: number
}

export class RestQuery<
  Filters,
  Ordering = undefined,
  Includes = undefined,
  Additional = undefined,
> implements Query {
  constructor(
    private paging: Paging | undefined = undefined,
    private filters: Filters = {} as Filters,
    private ordering: Ordering[] = [],
    private baseParams: Record<string, unknown> = {},
    private noPreload = false,
    private includes: Includes[] = [],
    private metaIncludes: AvailableInclude[] = []
  ) {}

  paged(
    length: number,
    page: number
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.paging = { length, page }
    return this
  }

  unpaged(): RestQuery<Filters, Ordering, Includes, Additional> {
    this.paging = undefined
    return this
  }

  filtered(
    filters: Filters
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.filters = { ...this.filters, ...filters }
    return this
  }

  ordered(
    ordering: Ordering[]
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.ordering = ordering
    return this
  }

  preload(
    preload: boolean
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.noPreload = !preload
    return this
  }

  include(
    include: Includes[]
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.includes = include
    return this
  }

  includeMeta(
    include: AvailableInclude[]
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.metaIncludes = include
    return this
  }

  additional(
    params: Additional
  ): RestQuery<Filters, Ordering, Includes, Additional> {
    this.baseParams = { ...this.baseParams, ...params }
    return this
  }

  get includedMeta(): AvailableInclude[] {
    return this.metaIncludes
  }

  compile(): RequestParams {
    return {
      ...this.baseParams,
      ...(this.filters && Object.keys(this.filters).length > 0
        ? { filter: this.filters }
        : {}),
      ...(this.paging
        ? {
            length: this.paging.length,
            page: this.paging.page,
          }
        : {}),
      ...(this.noPreload ? { no_preload: true } : {}),
      ...(this.includes.length ? { include: this.includes.join(',') } : {}),
      ...(this.ordering.length ? { sort: this.ordering.join(',') } : {}),
      ...(this.metaIncludes.length
        ? { include_meta: this.metaIncludes.join(',') }
        : {}),
    }
  }

  toString(): string {
    const query = buildRequestParams(
      new HttpParams(),
      this.compile(),
      ''
    ).toString()
    return query ? `?${query}` : ''
  }

  clone(): typeof this {
    return new (
      this.constructor as new (
        paging: Paging | undefined,
        filters: Filters,
        ordering: Ordering[],
        baseParams: Record<string, unknown>,
        noPreload: boolean,
        includes: Includes[],
        metaIncludes: AvailableInclude[]
      ) => typeof this
    )(
      this.paging,
      this.filters,
      this.ordering,
      this.baseParams,
      this.noPreload,
      this.includes,
      this.metaIncludes
    )
  }
}
